import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import useGuide from '@/utils/hooks/useGuide';
import GuideTarget from '@/components/Guides/GuideTarget';
import { ActivityIndicator, RefreshControl, ScrollView, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { useNavigation, useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { StyleSheet } from 'react-native-unistyles';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import SheetModal from '@/components/General/SheetModal';
import SolidButton from '@/components/General/SolidButton';
import FormCard from '@/components/Forms/FormCard';
import FormEntriesTab from '@/components/Forms/builder/FormEntriesTab';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { canEditForm, canFillForm, canManageFormEntries } from '@/utils/formPermissions';
import { buildFolderTree } from '@/utils/formFolders';

const compareNames = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' });

const FormList = ({ forms, folders, viewMode, renderForm, testID }) => {
    const [collapsedFolders, setCollapsedFolders] = useState({});
    const groups = useMemo(() => {
        const knownFolderIDs = new Set(folders.map((folder) => folder.id));
        const formsByFolder = new Map();
        forms.forEach((form) => {
            const folderID = knownFolderIDs.has(form.folderID) ? form.folderID : null;
            if (!formsByFolder.has(folderID)) formsByFolder.set(folderID, []);
            formsByFolder.get(folderID).push(form);
        });
        const populate = (items) => items.map((folder) => {
            const children = populate(folder.children);
            const items = formsByFolder.get(folder.id) || [];
            return { ...folder, items, children, count: items.length + children.reduce((sum, child) => sum + child.count, 0) };
        }).filter((folder) => folder.count > 0).sort((a, b) => compareNames(a.name, b.name));
        const tree = populate(buildFolderTree(folders));
        const unfiled = formsByFolder.get(null) || [];
        if (unfiled.length > 0) tree.push({ id: 'uncategorized', name: 'Uncategorized', items: unfiled, children: [], count: unfiled.length });
        return tree.sort((a, b) => compareNames(a.name, b.name));
    }, [folders, forms]);

    const renderFolder = (folder) => {
        const expanded = !collapsedFolders[folder.id];
        const countLabel = `${folder.count} ${folder.count === 1 ? 'form' : 'forms'}`;
        return <View key={folder.id} testID={`${testID}-folder-${folder.id}`} style={styles.folderGroup}>
            <TouchableOpacity testID={`${testID}-folder-toggle-${folder.id}`} accessibilityRole="button" accessibilityLabel={`${folder.name}, ${countLabel} available`} accessibilityState={{ expanded }} aria-expanded={expanded} onPress={() => setCollapsedFolders((previous) => ({ ...previous, [folder.id]: !previous[folder.id] }))} style={styles.folderHeader}>
                <Unicon name={expanded ? 'chevron-down' : 'chevron-forward'} size={16} colorVariant="textSecondary" />
                <Unicon name="folder-outline" size={18} colorVariant="forms" />
                <Text testID={`${testID}-folder-name-${folder.id}`} numberOfLines={1} style={styles.folderName}>{folder.name}</Text>
                <View testID={`${testID}-folder-count-${folder.id}`} style={styles.folderBadge}><Text style={styles.folderCount}>{folder.count}</Text></View>
            </TouchableOpacity>
            {expanded && <View style={styles.folderContent}>
                {folder.items.length > 0 && <View style={styles.cards}>{folder.items.map(renderForm)}</View>}
                {folder.children.map(renderFolder)}
            </View>}
        </View>;
    };

    return <View style={styles.cards}>{viewMode === 'folders' ? groups.map(renderFolder) : forms.map(renderForm)}</View>;
};

const SectionHeader = ({ eyebrow, title, actionLabel, onAction, testID }) => (
    <View style={styles.sectionHeader}>
        <View style={styles.sectionHeading}><Text style={styles.eyebrow}>{eyebrow}</Text><Text style={styles.sectionTitle}>{title}</Text></View>
        {!!onAction && <TouchableOpacity testID={testID} accessibilityRole="button" accessibilityLabel={`${actionLabel}: ${title}`} onPress={onAction} style={styles.sectionAction}><Text style={styles.sectionActionText}>{actionLabel}</Text><Unicon name="arrow-forward" size={14} colorVariant="forms" /></TouchableOpacity>}
    </View>
);

const FilterChip = ({ label, selected, onPress, testID }) => (
    <TouchableOpacity testID={testID} accessibilityRole="button" accessibilityState={{ selected }} aria-pressed={selected} accessibilityLabel={label} onPress={onPress} style={styles.filterChip(selected)}>
        <Text style={styles.filterChipText(selected)}>{label}</Text>
    </TouchableOpacity>
);

const Stat = ({ icon, value, label, tone = 'forms' }) => (
    <View style={styles.stat}><View style={styles.statIcon(tone)}><Unicon name={icon} size={16} colorVariant={tone} /></View><Text style={styles.statValue}>{value}</Text><Text style={styles.statLabel}>{label}</Text></View>
);

export default function FormsDashboardScreen() {
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const guideScrollRef = useRef(null);
    const { width } = useWindowDimensions();
    const desktop = width >= 900;
    const [entriesForm, setEntriesForm] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [viewMode, setViewMode] = useState('folders');
    const { data: user, isLoading: userLoading } = Query.people.loggedInUser();
    const { data: forms = [], isLoading: formsLoading, isFetching: formsFetching } = Query.forms.all();
    const { data: folders = [], isLoading: foldersLoading, isFetching: foldersFetching } = Query.formFolders.all();
    const sortedForms = useMemo(() => [...forms].sort((a, b) => compareNames(a.title || '', b.title || '')), [forms]);
    const { data: myEntries = [], isLoading: entriesLoading, isFetching: entriesFetching } = Query.formEntries.mine({ options: { enabled: !!user?.id } });
    const canCreate = user?.can?.('Create Forms');
    // The library is meaningful for every org member — RLS returns public
    // forms plus any form the member has a grant on.
    const canViewLibrary = !!user;

    // "My forms" = forms the member can manage: creator/admin or "Edit" grant.
    const myForms = useMemo(() => sortedForms.filter((form) => canEditForm(form, user)), [sortedForms, user]);
    const activeForms = useMemo(() => sortedForms.filter((form) => form.status === 'published'), [sortedForms]);
    const drafts = myForms.filter((form) => form.status === 'draft').length;
    const closed = myForms.filter((form) => form.status === 'closed').length;
    const archivedCount = myForms.filter((form) => form.status === 'archived').length;
    const visibleMyForms = useMemo(() => myForms.filter((form) => showArchived || form.status !== 'archived'), [myForms, showArchived]);

    // Most recent of the current user's entries per form, for the card footer link.
    const myEntryByFormID = useMemo(() => {
        const map = {};
        for (const entry of myEntries) {
            const existing = map[entry.formID];
            if (!existing || new Date(entry.createdAt) > new Date(existing.createdAt)) map[entry.formID] = entry;
        }
        return map;
    }, [myEntries]);

    const openMyEntry = useCallback((entry) => {
        router.push({ pathname: `/forms/entries/${entry.id}`, params: { source: 'mine' } });
    }, [router]);

    const refresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.forms.all() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.mine() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.formFolders.all() });
    }, [queryClient]);
    const loading = userLoading || formsLoading || foldersLoading || entriesLoading;
    const refreshing = !loading && (formsFetching || foldersFetching || entriesFetching);
    const guide = useGuide('forms.dashboard', { ready: !loading, forms, myEntries });

    useEffect(() => {
        navigation.setOptions({
            headerRight: () => <DropdownMenu testID="create-form-button" options={[
                ...(canCreate ? [
                    { name: 'Create Form', icon: dropdownIcons.add, onSelect: () => router.push('/forms/create') },
                    { name: 'Create Folder', icon: dropdownIcons.create, onSelect: () => router.push('/forms/folders/create') },
                ] : []),
                ...(guide ? [{ name: 'Tour this page', icon: dropdownIcons.help, onSelect: () => guide.replay('forms.dashboard') }] : []),
            ]} />,
        });
    }, [canCreate, navigation, router, guide]);

    const guideFillForm = activeForms.find(form => canFillForm(form, user));
    const guideEntryForm = activeForms.find(form => myEntryByFormID[form.id]);
    const guideEntriesForm = [...activeForms, ...visibleMyForms].find(form => canManageFormEntries(form, user));
    const formGuideTargets = (form, activeCard = false) => ({
        fill: activeCard && form.id === guideFillForm?.id ? 'forms.fill' : undefined,
        myEntry: activeCard && form.id === guideEntryForm?.id ? 'forms.myentries' : undefined,
        entries: form.id === guideEntriesForm?.id ? 'forms.entries' : undefined,
    });

    if (loading) return <View style={styles.loading}><ActivityIndicator size="large" /></View>;

    return (
        <ScrollView ref={guideScrollRef} testID="forms-dashboard" style={styles.root} contentContainerStyle={styles.content} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} />}>
            <View style={styles.hero}>
                <View style={styles.heroCopy}><Text style={styles.heroEyebrow}>COLLECT & RESPOND</Text><Text style={styles.heroTitle}>Forms Dashboard</Text><Text style={styles.heroText}>Build forms, fill active forms, and return to responses linked to you.</Text></View>
                <View style={{ gap: 8, alignItems: 'flex-end' }}>
                    {canCreate && <GuideTarget id="forms.create" scrollRef={guideScrollRef}><SolidButton testID="forms-dashboard-create" text="Create form" iconName="add" iconBefore type="forms" onPress={() => router.push('/forms/create')} /></GuideTarget>}
                </View>
            </View>

            <View style={styles.attentionStrip} testID="forms-dashboard-overview">
                <Text style={styles.attentionTitle}>Overview</Text>
                <View style={styles.stats}>
                    <Stat icon="create-outline" value={drafts} label="drafts" tone={drafts > 0 ? 'warning' : 'forms'} />
                    <Stat icon="checkmark-circle-outline" value={activeForms.length} label="active" />
                    <Stat icon="mail-open-outline" value={myEntries.length} label="linked entries" />
                    {closed > 0 && <Stat icon="archive-outline" value={closed} label="closed" tone="textSecondary" />}
                </View>
            </View>

            <View style={styles.viewControls}>
                <FilterChip label="View all" selected={viewMode === 'all'} onPress={() => setViewMode('all')} testID="forms-dashboard-view-all" />
                <FilterChip label="View folders" selected={viewMode === 'folders'} onPress={() => setViewMode('folders')} testID="forms-dashboard-view-folders" />
            </View>

            <View style={styles.columns(desktop)}>
                <View style={styles.primaryColumn(desktop)}>
                    {activeForms.length > 0 && <View testID="forms-dashboard-active-forms">
                        <SectionHeader eyebrow="PUBLISHED" title="Active forms" actionLabel="Open library" testID="forms-dashboard-active-library" onAction={canViewLibrary ? () => router.push('/forms/library') : null} />
                        <FormList forms={activeForms} folders={folders} viewMode={viewMode} testID="forms-dashboard-active" renderForm={(form) => {
                            const menuOptions = [];
                            if (canManageFormEntries(form, user)) {
                                menuOptions.push({ name: 'View Entries', icon: dropdownIcons.unhide, onSelect: () => setEntriesForm(form) });
                            }
                            const canSubmit = canFillForm(form, user);
                            return <FormCard key={form.id} form={form} guideTargets={formGuideTargets(form, true)} guideScrollRef={guideScrollRef} onPress={() => router.push(`/forms/${form.id}`)} actionLabel={canSubmit ? 'Fill out' : undefined} onAction={canSubmit ? () => router.push({ pathname: `/forms/${form.id}`, params: { mode: 'fill' } }) : undefined} menuOptions={menuOptions} showEntryCount={canManageFormEntries(form, user)} myEntry={myEntryByFormID[form.id]} onMyEntryPress={openMyEntry} />;
                        }} />
                    </View>}

                    {activeForms.length === 0 && myForms.length === 0 && myEntries.length === 0 && (
                        <View style={styles.permissionState}><Unicon name="document-text-outline" size={24} colorVariant="forms" /><Text style={styles.permissionTitle}>{canCreate ? 'Create your first form' : 'No forms available'}</Text><Text style={styles.permissionText}>{canCreate ? 'Start with a form, then publish it when it is ready to collect responses.' : 'You do not currently have access to fill or edit forms. Your linked entries will appear here when available.'}</Text>{canCreate && <SolidButton text="Create form" type="forms" onPress={() => router.push('/forms/create')} testID="forms-empty-create" />}</View>
                    )}
                </View>

                <View style={styles.secondaryColumn(desktop)}>
                    {(myForms.length > 0 || canCreate) && <GuideTarget id="forms.manage" testID="forms-dashboard-my-forms" scrollRef={guideScrollRef}>
                        <SectionHeader eyebrow="MANAGE" title="My forms" actionLabel="Open library" testID="forms-dashboard-library" onAction={() => router.push('/forms/library')} />
                        {archivedCount > 0 && <View style={styles.filterRow}><FilterChip label={`Show Archived (${archivedCount})`} selected={showArchived} onPress={() => setShowArchived((v) => !v)} testID="forms-dashboard-show-archived" /></View>}
                        {visibleMyForms.length > 0 ? <FormList forms={visibleMyForms} folders={folders} viewMode={viewMode} testID="forms-dashboard-my" renderForm={(form) => {
                            const menuOptions = [];
                            if (canManageFormEntries(form, user)) {
                                menuOptions.push({ name: 'View Entries', icon: dropdownIcons.unhide, onSelect: () => setEntriesForm(form) });
                            }
                            return <FormCard key={form.id} form={form} guideTargets={formGuideTargets(form)} guideScrollRef={guideScrollRef} onPress={() => router.push(`/forms/${form.id}`)} menuOptions={menuOptions} showEntryCount={canManageFormEntries(form, user)} />;
                        }} /> : <View style={styles.compactEmpty}><Text style={styles.compactEmptyText}>{myForms.length > 0 ? 'All managed forms are archived.' : 'No forms to manage yet.'}</Text>{myForms.length === 0 && <SolidButton text="Create form" size="sm" type="forms" variant="muted" onPress={() => router.push('/forms/create')} testID="forms-my-empty-create" />}</View>}
                    </GuideTarget>}

                    {canViewLibrary && <GuideTarget id="forms.library" scrollRef={guideScrollRef}><TouchableOpacity testID="forms-dashboard-browse-library" accessibilityRole="button" accessibilityLabel="Open form library" style={styles.browseCard} onPress={() => router.push('/forms/library')}><View><Text style={styles.browseEyebrow}>BROWSE</Text><Text style={styles.browseTitle}>Form library</Text><Text style={styles.browseText}>Search, filter, and organize all visible forms.</Text></View><Unicon name="arrow-forward" size={19} colorVariant="forms" /></TouchableOpacity></GuideTarget>}
                </View>
            </View>

            <SheetModal
                isOpen={!!entriesForm}
                title={entriesForm ? `Entries: ${entriesForm.title}` : 'Entries'}
                cancelText="Close"
                width={1600}
                onCancel={() => setEntriesForm(null)}
                onClose={() => setEntriesForm(null)}
            >
                {entriesForm && (
                    <FormEntriesTab
                        form={entriesForm}
                        formID={entriesForm.id}
                        canManageEntries={canManageFormEntries(entriesForm, user)}
                        router={router}
                    />
                )}
            </SheetModal>
        </ScrollView>
    );
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, backgroundColor: 'transparent' },
    loading: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent' },
    content: { width: '100%', maxWidth: 1240, alignSelf: 'center', paddingHorizontal: 16, paddingTop: 18, paddingBottom: 110, gap: 16 },
    hero: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 18, paddingVertical: 6 },
    heroCopy: { flex: 1, minWidth: 260, maxWidth: 650 },
    heroEyebrow: { color: theme.colors.forms, fontFamily: theme.fonts.bold, fontSize: 11, letterSpacing: 1.2 },
    heroTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 28, lineHeight: 34, marginTop: 5 },
    heroText: { color: theme.colors.textSecondary, fontSize: 14, lineHeight: 20, marginTop: 5 },
    attentionStrip: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 14, backgroundColor: theme.colors.surface, borderRadius: 14, paddingHorizontal: 16, paddingVertical: 11, boxShadow: theme.shadows.card },
    attentionTitle: { color: theme.colors.text, fontFamily: theme.fonts.bold, fontSize: 13 },
    stats: { flex: 1, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 16 },
    stat: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    statIcon: (tone) => ({ width: 28, height: 28, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors[`${tone}Container`] || theme.colors.formsContainer }),
    statValue: { color: theme.colors.text, fontFamily: theme.fonts.bold, fontSize: 15 },
    statLabel: { color: theme.colors.textSecondary, fontSize: 12 },
    columns: (desktop) => ({ flexDirection: desktop ? 'row' : 'column', alignItems: 'flex-start', gap: 22 }),
    primaryColumn: (desktop) => ({ width: desktop ? undefined : '100%', flex: desktop ? 1.2 : undefined, minWidth: 0 }),
    secondaryColumn: (desktop) => ({ width: desktop ? undefined : '100%', flex: desktop ? 0.8 : undefined, minWidth: 0, gap: 22 }),
    sectionHeader: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 10 },
    sectionHeading: { flex: 1 },
    eyebrow: { color: theme.colors.forms, fontFamily: theme.fonts.bold, fontSize: 10, letterSpacing: 1.1 },
    sectionTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 19, marginTop: 2 },
    sectionAction: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 5 },
    sectionActionText: { color: theme.colors.forms, fontFamily: theme.fonts.bold, fontSize: 12 },
    cards: { gap: 9 },
    viewControls: { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
    folderGroup: { gap: 9 },
    folderHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, minHeight: 44, paddingHorizontal: 12, paddingVertical: 10 },
    folderName: { flexShrink: 1, color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 14 },
    folderBadge: { minWidth: 24, height: 24, paddingHorizontal: 7, borderRadius: 12, flexShrink: 0, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors.formsContainer },
    folderCount: { color: theme.colors.forms, fontFamily: theme.fonts.bold, fontSize: 12, fontVariant: ['tabular-nums'] },
    folderContent: { gap: 9, paddingLeft: 12 },
    filterRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 7, marginBottom: 10 },
    filterChip: (selected) => ({ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8, backgroundColor: selected ? theme.colors.formsContainer : theme.colors.surface }),
    filterChipText: (selected) => ({ color: selected ? theme.colors.forms : theme.colors.textSecondary, fontFamily: theme.fonts.bold, fontSize: 11 }),
    compactEmpty: { backgroundColor: theme.colors.surface, borderRadius: 14, padding: 16, gap: 10, boxShadow: theme.shadows.card },
    compactEmptyText: { color: theme.colors.textSecondary, fontSize: 13 },
    permissionState: { alignItems: 'center', backgroundColor: theme.colors.surface, borderRadius: 14, padding: 24, gap: 8, boxShadow: theme.shadows.card },
    permissionTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 17 },
    permissionText: { color: theme.colors.textSecondary, fontSize: 13, lineHeight: 19, textAlign: 'center', maxWidth: 480 },
    browseCard: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, backgroundColor: theme.colors.formsContainer, borderRadius: 14, padding: 15 },
    browseEyebrow: { color: theme.colors.forms, fontFamily: theme.fonts.bold, fontSize: 10, letterSpacing: 1.1 },
    browseTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 15, marginTop: 3 },
    browseText: { color: theme.colors.textSecondary, fontSize: 11, marginTop: 2 },
}));
